import React from "react";
import * as moment from "moment-timezone";
import { ReporteChequeo, ReporteRow } from "../../../../models/ReporteChequeo";
import { calcularTiempoTrabajado } from "../../../../interfaces/formik/IReporteRelojChecador.ts";
import { operationalDayKeyForVisitRow } from "../../../../utils/operationalDayReporteChequeo.ts";
import type { BranchReportMeta } from "../../../../utils/payrollBranchMeta.ts";
import {
  reporteChequeoTableUi as S,
  REPORTE_CHEQUEO_DAY_SUM_LABEL_COLSPAN,
} from "./reporteChequeoTableUi.ts";

const resolveRowDateKey = (
  row: ReporteRow,
  payrollBranchMeta?: Record<number, BranchReportMeta>,
  allChecks: ReporteChequeo[] = []
) =>
  operationalDayKeyForVisitRow(row, payrollBranchMeta, allChecks) ||
  (row.checkIn?.assistanceCheck_checkDate
    ? moment.utc(row.checkIn.assistanceCheck_checkDate).format("YYYY-MM-DD")
    : "");

const RowHourSum = ({
  records,
  index,
  payrollBranchMeta,
  allChecks = [],
}: {
  records: ReporteRow[];
  index: number;
  payrollBranchMeta?: Record<number, BranchReportMeta>;
  allChecks?: ReporteChequeo[];
}) => {
  const [totalHours, setTotalHours] = React.useState(0);
  const currentPartnerId = records[index].checkIn?.partner_id;
  const nextPartnerId = records[index + 1]?.checkIn?.partner_id;

  const currentDateKey = resolveRowDateKey(
    records[index],
    payrollBranchMeta,
    allChecks
  );
  const tomorrowDateKey = records[index + 1]
    ? resolveRowDateKey(records[index + 1], payrollBranchMeta, allChecks)
    : "";

  const isLast =
    Boolean(currentDateKey) &&
    (!tomorrowDateKey ||
      currentDateKey !== tomorrowDateKey ||
      currentPartnerId !== nextPartnerId);

  React.useEffect(() => {
    if (!currentDateKey) {
      setTotalHours(0);
      return;
    }

    let startIndex = index;
    while (startIndex > 0) {
      const previous = records[startIndex - 1];
      const previousDateKey = resolveRowDateKey(
        previous,
        payrollBranchMeta,
        allChecks
      );
      const previousPartnerId = previous.checkIn?.partner_id;

      if (
        previousDateKey !== currentDateKey ||
        previousPartnerId !== currentPartnerId
      ) {
        break;
      }

      startIndex -= 1;
    }

    const groupRecords = records.slice(startIndex, index + 1);
    setTotalHours(
      groupRecords.reduce((acc, record) => {
        const time = calcularTiempoTrabajado(
          record.checkIn?.assistanceCheck_checkTime!,
          record.checkOut?.assistanceCheck_checkTime
        );
        return acc + Number(time) || 0;
      }, 0)
    );
  }, [records, currentDateKey, currentPartnerId, index, payrollBranchMeta, allChecks]);

  if (!isLast) {
    return null;
  }

  return (
    <tr style={S.daySumRow}>
      <td colSpan={REPORTE_CHEQUEO_DAY_SUM_LABEL_COLSPAN} style={S.daySumLabel}>
        Horas totales{" "}
        {moment
          .utc(currentDateKey, "YYYY-MM-DD")
          .locale("es")
          .format("[del] DD MMMM YYYY")}
      </td>
      <td style={S.daySumHours}>{totalHours.toFixed(1)}</td>
      <td style={S.daySumCellEmpty} />
      <td style={S.daySumCellEmptyLast} />
    </tr>
  );
};

export default RowHourSum;
